import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";

// Helper functions
const formatDate = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleDateString();
};

const formatDateAndTime = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleString();
};

const moneyFormat = (amount: number) => {
	if (!amount && amount !== 0) return "0.00";
	return amount.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

export const getReimbursementStatusColor = (status: string) => {
	switch (status) {
		case "APPROVED":
			return "#4CAF50";
		case "COMPLETED":
			return "#808080";
		case "REJECTED":
			return "#F44336";
		case "SUBMITTED":
			return "#FFC107";
		case "QUERIED":
			return "#2196F3";
		default:
			return "#ADD8E6";
	}
};

// ==================== COMMON COLUMNS (BASE) ====================
const commonColumns: ColumnDef<any>[] = [
	{
		accessorKey: "title",
		header: "Title",
		size: 250,
		minSize: 200,
		maxSize: 400,
	},
	{
		accessorKey: "description",
		header: "Description",
		size: 400,
		minSize: 300,
		maxSize: 600,
	},
	{
		accessorKey: "amount",
		header: "Amount",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ row }) => {
			const amount = row.original.amount;
			const currency = row.original.currency?.currency?.code;
			return `${currency || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		accessorKey: "status",
		header: "Status",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const status = getValue() as string;
			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getReimbursementStatusColor(status) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getReimbursementStatusColor(status),
							opacity: 0.15,
						}}
					/>
					<p>{enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "expenditureDate",
		header: "Expenditure Date",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Last Date Modified",
		size: 180,
		minSize: 150,
		maxSize: 280,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// ==================== YOUR CLAIMS TABLE COLUMNS ====================
export const yourClaimsTableColumns: ColumnDef<any>[] = [...commonColumns];

// ==================== SUPERVISEES CLAIMS COLUMNS ====================
export const superviseesClaimsColumns: ColumnDef<any>[] = [
	{
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		minSize: 200,
		maxSize: 400,
		cell: ({ getValue }) => {
			const employee = getValue() as any;
			if (!employee) return "--";
			return (
				<div className="flex items-center space-x-4">
					<img
						src={employee?.photoUrl || "https://via.placeholder.com/40"}
						alt="Employee"
						className="w-10 h-10 rounded-full object-cover"
					/>
					<p className="truncate">
						{(employee?.firstName || "") + " " + (employee?.lastName || "")}
					</p>
				</div>
			);
		},
	},
	...commonColumns,
];
